<template>
  <div class="image-upload-wall">
    <!-- 图片列表 -->
    <div class="image-list">
      <div
        v-for="(item, index) in imageList"
        :key="item.uid || index"
        class="image-item"
        :class="{
          'is-primary': item.isPrimary,
          'is-disabled': item.attachmentStatus === 1,
          dragging: dragIndex === index,
          'drag-over': dragOverIndex === index,
        }"
        :draggable="item.attachmentStatus !== 1"
        @dragstart="handleDragStart(index, $event)"
        @dragover.prevent="handleDragOver(index, $event)"
        @dragenter.prevent="handleDragEnter(index, $event)"
        @dragleave="handleDragLeave(index, $event)"
        @drop="handleDrop(index, $event)"
        @dragend="handleDragEnd"
      >
        <div class="image-wrapper" @click.stop>
          <img :src="item.url" :alt="item.name" />
          <!-- 悬停遮罩层 -->
          <div class="image-overlay">
            <!-- 预览按钮 - 禁用状态下隐藏 -->
            <a-tooltip v-if="item.attachmentStatus !== 1" title="预览">
              <div
                class="action-icon-btn preview-icon-btn"
                @click.stop="handlePreview(item)"
                @mousedown.stop
                @dragstart.stop
              >
                <eye-outlined />
              </div>
            </a-tooltip>
            <!-- 启用/禁用开关 - 始终显示 -->
            <a-tooltip :title="item.attachmentStatus === 0 ? '禁用' : '启用'">
              <div
                class="action-icon-btn status-icon-btn"
                @click.stop="handleToggleStatus(index)"
                @mousedown.stop
                @dragstart.stop
              >
                <a-switch
                  :checked="item.attachmentStatus === 0"
                  size="small"
                  :checked-value="0"
                  :unchecked-value="1"
                  @click.stop
                  @change.stop
                />
              </div>
            </a-tooltip>
            <!-- 删除按钮 - 禁用状态下隐藏 -->
            <a-tooltip v-if="item.attachmentStatus !== 1" title="删除">
              <div
                class="action-icon-btn delete-icon-btn"
                @click.stop="handleRemove(index)"
                @mousedown.stop
                @dragstart.stop
              >
                <delete-outlined />
              </div>
            </a-tooltip>
            <!-- 设为主图按钮 - 禁用状态下隐藏 -->
            <a-tooltip v-if="!item.isPrimary && item.attachmentStatus !== 1" title="设为主图">
              <div
                class="action-icon-btn set-primary-icon-btn"
                @click.stop="handleSetPrimary(index)"
                @mousedown.stop
                @dragstart.stop
              >
                <check-circle-outlined />
              </div>
            </a-tooltip>
          </div>
          <div v-if="item.isPrimary" class="primary-badge">主图</div>
          <div v-if="item.attachmentStatus === 1" class="disabled-badge">已禁用</div>
        </div>
        <div class="image-name" :title="item.name">{{ item.name }}</div>
        <div
          v-if="compressBeforeUpload && item.originalSize !== undefined"
          class="px-1 py-1 text-center text-xs text-gray-500"
        >
          <div>原始：{{ formatImageSize(item.originalSize) }}</div>
          <div v-if="item.compressedSize !== undefined">
            压缩后：{{ formatImageSize(item.compressedSize) }}
          </div>
          <div v-else-if="item.compressionFailed">压缩失败，已上传原图</div>
          <div v-else>无需压缩</div>
        </div>
        <div v-else-if="compressBeforeUpload" class="px-1 py-1 text-center text-xs text-gray-500">
          <template v-if="item.fileInfo?.size !== undefined">
            当前大小：{{ formatImageSize(item.fileInfo.size) }}
          </template>
          <template v-else-if="existingImageSizes.get(item.url) === null">
            当前大小：无法获取
          </template>
          <template v-else-if="existingImageSizes.get(item.url) !== undefined">
            当前大小：{{ formatImageSize(existingImageSizes.get(item.url) ?? 0) }}
          </template>
          <template v-else>当前大小：读取中</template>
        </div>
      </div>

      <!-- 上传按钮放在图片列表后面 -->
      <a-upload
        v-if="imageList.length < maxCount"
        list-type="picture-card"
        :before-upload="beforeUpload"
        :custom-request="handleCustomRequest"
        :multiple="true"
        accept="image/*"
        :show-upload-list="false"
        class="upload-item"
        @change="handleUploadChange"
      >
        <div class="upload-btn">
          <plus-outlined />
          <div class="upload-text">上传图片</div>
        </div>
      </a-upload>
    </div>

    <!-- 预览模态框 -->
    <a-modal
      v-model:open="previewVisible"
      :footer="null"
      centered
      :width="800"
      class="preview-modal"
    >
      <div class="preview-container" tabindex="0" @keydown="handlePreviewKeydown">
        <!-- 图片区域 -->
        <div class="preview-image-wrapper">
          <img :src="previewImage" class="preview-image" alt="预览" />
          <!-- 图片信息 -->
          <div v-if="imageList.length > 1" class="preview-info">
            {{ currentPreviewIndex + 1 }} / {{ imageList.length }}
          </div>
        </div>
        <!-- 上一张按钮 -->
        <div
          v-if="imageList.length > 1"
          class="preview-nav-btn prev-btn"
          :class="{ disabled: currentPreviewIndex === 0 }"
          @click="handlePrevImage"
        >
          <left-outlined />
        </div>
        <!-- 下一张按钮 -->
        <div
          v-if="imageList.length > 1"
          class="preview-nav-btn next-btn"
          :class="{ disabled: currentPreviewIndex === imageList.length - 1 }"
          @click="handleNextImage"
        >
          <right-outlined />
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
  import { ref, watch, computed, nextTick, onMounted, onUnmounted } from 'vue';
  import { message } from 'ant-design-vue';
  import {
    PlusOutlined,
    EyeOutlined,
    StarOutlined,
    StarFilled,
    DeleteOutlined,
    LeftOutlined,
    RightOutlined,
    CheckCircleOutlined,
  } from '@ant-design/icons-vue';
  import type { UploadProps } from 'ant-design-vue';
  import { uploadFile, type UploadedFileInfo } from '@/service/fileUtil';
  import {
    compressImage,
    formatImageSize,
    fetchImageSize,
    IMAGE_COMPRESSION_THRESHOLD,
    IMAGE_UPLOAD_WARNING_THRESHOLD,
  } from '@/utils/imageCompression';

  export interface ImageItem {
    uid: string;
    name: string;
    url: string;
    fileInfo?: UploadedFileInfo; // 上传后的文件信息
    isPrimary?: boolean; // 是否为主图
    sort?: number; // 排序字段
    attachmentStatus?: number; // 附件状态：0启用，1禁用
    originalSize?: number;
    compressedSize?: number;
    compressionFailed?: boolean;
  }

  interface Props {
    modelValue?: ImageItem[];
    maxCount?: number; // 最大上传数量
    compressBeforeUpload?: boolean;
  }

  interface Emits {
    (e: 'update:modelValue', value: ImageItem[]): void;
    (e: 'change', value: ImageItem[]): void;
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: () => [],
    maxCount: 10,
    compressBeforeUpload: false,
  });

  const emit = defineEmits<Emits>();

  const imageList = ref<ImageItem[]>([]);
  const existingImageSizes = ref(new Map<string, number | null>());
  const pendingImageSizes = new Set<string>();
  const imageSizeController = new AbortController();

  onUnmounted(() => imageSizeController.abort());

  watch(
    () =>
      props.compressBeforeUpload
        ? imageList.value
            .filter((item) => item.originalSize === undefined && item.fileInfo?.size === undefined)
            .map((item) => item.url)
        : [],
    (urls) => {
      urls.forEach(async (url) => {
        if (existingImageSizes.value.has(url) || pendingImageSizes.has(url)) return;
        pendingImageSizes.add(url);
        try {
          const size = await fetchImageSize(url, imageSizeController.signal);
          existingImageSizes.value.set(url, size);
        } catch (error) {
          if (!imageSizeController.signal.aborted) {
            console.warn('获取已有图片大小失败:', url, error);
            existingImageSizes.value.set(url, null);
          }
        } finally {
          pendingImageSizes.delete(url);
        }
      });
    },
    { immediate: true },
  );
  const previewVisible = ref(false);
  const previewImage = ref('');
  const currentPreviewIndex = ref(0);
  const dragIndex = ref<number | null>(null);
  const dragOverIndex = ref<number | null>(null);

  // 是否正在更新内部列表（防止循环更新）
  const isUpdatingInternal = ref(false);

  // 深度比较两个数组是否相同
  const isArrayEqual = (arr1: ImageItem[], arr2: ImageItem[]): boolean => {
    if (arr1.length !== arr2.length) {
      return false;
    }
    if (arr1.length === 0 && arr2.length === 0) {
      return true;
    }
    return arr1.every(
      (item, index) =>
        item.uid === arr2[index]?.uid &&
        item.url === arr2[index]?.url &&
        item.isPrimary === arr2[index]?.isPrimary &&
        item.attachmentStatus === arr2[index]?.attachmentStatus,
    );
  };

  // 监听外部传入的 modelValue
  watch(
    () => props.modelValue,
    (newVal) => {
      // 如果正在更新内部列表，跳过此次更新
      if (isUpdatingInternal.value) {
        return;
      }

      const newList = newVal || [];
      const currentList = imageList.value;

      // 如果内容相同，不需要更新
      if (isArrayEqual(currentList, newList)) {
        return;
      }

      if (newList.length > 0) {
        // 确保只有一张图片是主图
        const list = [...newList];
        let primaryFound = false;
        list.forEach((item, index) => {
          if (item.isPrimary) {
            if (primaryFound) {
              // 如果已经找到主图，将后续的主图标记取消
              item.isPrimary = false;
            } else {
              primaryFound = true;
            }
          }
        });
        // 如果没有主图，设置第一张为主图
        if (!primaryFound && list.length > 0) {
          list[0].isPrimary = true;
        }
        imageList.value = list;
      } else {
        imageList.value = [];
      }
    },
    { immediate: true, deep: true },
  );

  // 监听内部 imageList 变化，同步到外部
  watch(
    imageList,
    (newVal) => {
      // 如果正在更新内部列表，跳过此次更新
      if (isUpdatingInternal.value) {
        return;
      }

      const externalList = props.modelValue || [];
      // 如果内容相同，不需要触发更新
      if (isArrayEqual(newVal, externalList)) {
        return;
      }

      // 更新排序字段
      newVal.forEach((item, index) => {
        item.sort = index + 1;
      });

      // 标记正在更新，防止循环
      isUpdatingInternal.value = true;
      emit('update:modelValue', [...newVal]);
      emit('change', [...newVal]);
      // 使用 nextTick 确保更新完成后再重置标志
      nextTick(() => {
        isUpdatingInternal.value = false;
      });
    },
    { deep: true },
  );

  // 上传前的校验
  const beforeUpload: UploadProps['beforeUpload'] = (file) => {
    console.log('🔍 [beforeUpload] 开始校验文件:', {
      name: file.name,
      type: file.type,
      size: file.size,
      currentImageCount: imageList.value.length,
      maxCount: props.maxCount,
    });

    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      console.log('❌ [beforeUpload] 文件类型不符合要求');
      message.error('只能上传图片文件！');
      return false;
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!props.compressBeforeUpload && !isLt10M) {
      console.log('❌ [beforeUpload] 文件大小超过限制');
      message.error('图片大小不能超过 10MB！');
      return false;
    }
    if (imageList.value.length >= props.maxCount) {
      console.log('❌ [beforeUpload] 图片数量已达上限');
      message.warning(`最多只能上传 ${props.maxCount} 张图片！`);
      return false;
    }
    console.log('✅ [beforeUpload] 校验通过，允许上传');
    // 返回 true 允许继续，custom-request 会被调用
    return true;
  };

  // 自定义上传
  const handleCustomRequest: UploadProps['customRequest'] = async (options) => {
    console.log('🚀 [handleCustomRequest] 开始自定义上传', options);
    const { file, onSuccess, onError, onProgress } = options;

    if (!(file instanceof File)) {
      console.error('❌ [handleCustomRequest] 文件对象为空');
      message.error('文件对象为空，无法上传');
      onError?.(new Error('文件对象为空'));
      return;
    }

    try {
      console.log('📤 [handleCustomRequest] 准备上传文件:', {
        name: file.name,
        type: file.type,
        size: file.size,
      });

      // 显示上传进度
      onProgress?.({ percent: 10 });
      console.log('📊 [handleCustomRequest] 上传进度: 10%');

      const shouldCompress = props.compressBeforeUpload && file.size > IMAGE_COMPRESSION_THRESHOLD;
      let uploadImage = file;
      let compressionFailed = false;
      if (shouldCompress) {
        try {
          uploadImage = await compressImage(file);
          if (uploadImage.size > IMAGE_UPLOAD_WARNING_THRESHOLD) {
            message.warning(
              `${file.name} 压缩后仍超过 1 MB（${formatImageSize(uploadImage.size)}），将继续上传`,
              5,
            );
          }
        } catch (error) {
          console.error('图片压缩失败，将上传原图:', error);
          compressionFailed = true;
          message.warning(`${file.name} 压缩失败，将继续上传原图`, 5);
        }
      }
      const formData = new FormData();
      formData.append('file', uploadImage);
      console.log('📦 [handleCustomRequest] FormData 已创建');

      onProgress?.({ percent: 30 });
      console.log('📊 [handleCustomRequest] 上传进度: 30%');

      console.log('🌐 [handleCustomRequest] 开始调用 uploadFile API...');
      const resp: UploadedFileInfo = await uploadFile(formData);
      console.log('✅ [handleCustomRequest] API 调用成功，响应:', resp);

      onProgress?.({ percent: 90 });
      console.log('📊 [handleCustomRequest] 上传进度: 90%');

      // 创建图片项
      const imageItem: ImageItem = {
        uid: `${Date.now()}-${Math.random()}`,
        name: resp.originalName || file.name,
        url: resp.viewUrl,
        fileInfo: resp,
        isPrimary: imageList.value.length === 0, // 第一张自动设为主图
        attachmentStatus: 0, // 默认启用
        originalSize: file.size,
        compressedSize: shouldCompress && !compressionFailed ? uploadImage.size : undefined,
        compressionFailed,
      };

      console.log('🖼️ [handleCustomRequest] 创建图片项:', imageItem);
      imageList.value.push(imageItem);
      console.log('📋 [handleCustomRequest] 当前图片列表:', imageList.value);

      onProgress?.({ percent: 100 });
      console.log('📊 [handleCustomRequest] 上传进度: 100%');
      message.success('图片上传成功');
      onSuccess?.(resp);
      console.log('🎉 [handleCustomRequest] 上传完成');
    } catch (error) {
      console.error('❌ [handleCustomRequest] 图片上传失败:', error);
      console.error('❌ [handleCustomRequest] 错误详情:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      const uploadError = error instanceof Error ? error : new Error('图片上传失败，请稍后重试');
      message.error(uploadError.message);
      onError?.(uploadError);
    }
  };

  // 设置主图
  const handleSetPrimary = (index: number) => {
    // 取消其他主图
    imageList.value.forEach((item) => {
      item.isPrimary = false;
    });
    // 设置新的主图
    imageList.value[index].isPrimary = true;
    message.success('已设置为主图');
  };

  // 切换启用/禁用状态
  const handleToggleStatus = (index: number) => {
    // 确保状态值是数字类型
    const currentStatus = Number(imageList.value[index].attachmentStatus ?? 0);
    imageList.value[index].attachmentStatus = currentStatus === 0 ? 1 : 0;
    // watch 会自动触发更新，不需要手动调用
    message.success(imageList.value[index].attachmentStatus === 0 ? '已启用' : '已禁用');
  };

  // 删除图片
  const handleRemove = (index: number) => {
    const removed = imageList.value[index];
    imageList.value.splice(index, 1);

    // 如果删除的是主图，且还有其他图片，自动设置第一张为主图
    if (removed.isPrimary && imageList.value.length > 0) {
      imageList.value[0].isPrimary = true;
    }

    message.success('已删除图片');
  };

  // 预览图片
  const handlePreview = (item: ImageItem) => {
    const index = imageList.value.findIndex((img) => img.uid === item.uid);
    currentPreviewIndex.value = index >= 0 ? index : 0;
    previewImage.value = item.url;
    previewVisible.value = true;
  };

  // 切换到上一张
  const handlePrevImage = () => {
    if (currentPreviewIndex.value > 0) {
      currentPreviewIndex.value--;
      previewImage.value = imageList.value[currentPreviewIndex.value].url;
    }
  };

  // 切换到下一张
  const handleNextImage = () => {
    if (currentPreviewIndex.value < imageList.value.length - 1) {
      currentPreviewIndex.value++;
      previewImage.value = imageList.value[currentPreviewIndex.value].url;
    }
  };

  // 键盘事件处理
  const handlePreviewKeydown = (e: KeyboardEvent) => {
    if (!previewVisible.value) return;

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      handlePrevImage();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      handleNextImage();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      previewVisible.value = false;
    }
  };

  // 监听预览模态框打开时，添加全局键盘事件
  watch(previewVisible, (isVisible) => {
    if (isVisible) {
      // 模态框打开时，聚焦到容器以便接收键盘事件
      nextTick(() => {
        const container = document.querySelector('.preview-container');
        if (container) {
          (container as HTMLElement).focus();
        }
      });
    }
  });

  // 上传组件 change 事件
  const handleUploadChange = (info: any) => {
    console.log('📝 [handleUploadChange] 上传组件 change 事件触发:', info);
    const { file, fileList } = info;
    console.log('📝 [handleUploadChange] 文件信息:', {
      name: file?.name,
      status: file?.status,
      percent: file?.percent,
    });
    console.log('📝 [handleUploadChange] 文件列表:', fileList);
  };

  // 拖拽排序相关方法
  const handleDragStart = (index: number, event: DragEvent) => {
    // 如果图片被禁用，不允许拖拽
    if (imageList.value[index].attachmentStatus === 1) {
      event.preventDefault();
      return;
    }

    // 如果点击的是按钮，不触发拖拽
    const target = event.target as HTMLElement;
    if (target.closest('.action-icon-btn')) {
      event.preventDefault();
      return;
    }

    dragIndex.value = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/html', String(index));
    }
  };

  const handleDragOver = (index: number, event: DragEvent) => {
    // 如果目标图片被禁用，不允许拖拽到此处
    if (imageList.value[index].attachmentStatus === 1) {
      return;
    }
    event.preventDefault();
    if (dragIndex.value !== null && dragIndex.value !== index) {
      dragOverIndex.value = index;
    }
  };

  const handleDragEnter = (index: number, event: DragEvent) => {
    // 如果目标图片被禁用，不允许拖拽到此处
    if (imageList.value[index].attachmentStatus === 1) {
      return;
    }
    event.preventDefault();
    if (dragIndex.value !== null && dragIndex.value !== index) {
      dragOverIndex.value = index;
    }
  };

  const handleDragLeave = (index: number, event: DragEvent) => {
    // 只有当离开的是当前悬停的元素时才清除
    if (dragOverIndex.value === index) {
      dragOverIndex.value = null;
    }
  };

  const handleDrop = (index: number, event: DragEvent) => {
    event.preventDefault();
    // 如果目标图片被禁用，不允许拖拽到此处
    if (imageList.value[index].attachmentStatus === 1) {
      dragIndex.value = null;
      dragOverIndex.value = null;
      return;
    }
    if (dragIndex.value === null || dragIndex.value === index) {
      return;
    }

    const draggedItem = imageList.value[dragIndex.value];
    // 如果被拖拽的图片被禁用，不允许拖拽
    if (draggedItem.attachmentStatus === 1) {
      dragIndex.value = null;
      dragOverIndex.value = null;
      return;
    }

    const newList = [...imageList.value];

    // 移除被拖拽的元素
    newList.splice(dragIndex.value, 1);
    // 插入到新位置
    newList.splice(index, 0, draggedItem);

    // 更新列表
    imageList.value = newList;

    // 重置拖拽状态
    dragIndex.value = null;
    dragOverIndex.value = null;
  };

  const handleDragEnd = () => {
    dragIndex.value = null;
    dragOverIndex.value = null;
  };

  // 暴露方法供父组件调用
  defineExpose({
    getImageList: () => imageList.value,
    getPrimaryImage: () => imageList.value.find((item) => item.isPrimary),
    clearAll: () => {
      imageList.value = [];
    },
  });
</script>

<style scoped lang="less">
  .image-upload-wall {
    .upload-btn {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      color: #8c8c8c;
      font-size: 14px;
      cursor: pointer;
      transition: color 0.3s;

      &:hover {
        color: #1890ff;
      }

      .anticon {
        font-size: 32px;
        margin-bottom: 8px;
      }

      .upload-text {
        margin-top: 4px;
      }
    }

    .image-list {
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      align-items: flex-start;

      .image-item {
        position: relative;
        width: 104px;
        border: 2px solid #d9d9d9;
        border-radius: 4px;
        overflow: hidden;
        transition: all 0.3s;
        cursor: move;

        &.is-primary {
          border-color: #1890ff;
          box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
        }

        &.is-disabled {
          opacity: 0.6;
          filter: grayscale(0.5);
          cursor: not-allowed !important;

          .image-wrapper {
            &::after {
              content: '';
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background: rgba(0, 0, 0, 0.2);
              z-index: 10;
              pointer-events: none;
            }
          }
        }

        &:hover {
          border-color: #1890ff;
        }

        &.dragging {
          opacity: 0.5;
          cursor: move;
        }

        &.drag-over {
          border-color: #1890ff;
          box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.4);
          transform: scale(1.05);
        }

        &[draggable='true'] {
          user-select: none;

          &:hover {
            cursor: move;
          }
        }

        &.dragging {
          opacity: 0.5;
        }

        &[draggable='true'] {
          user-select: none;
        }

        .image-wrapper {
          position: relative;
          width: 100%;
          height: 104px;
          overflow: hidden;
          background: #fafafa;
          cursor: default;

          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            pointer-events: none;
          }

          &:hover .image-overlay {
            opacity: 1 !important;
          }

          .image-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            opacity: 0;
            transition: opacity 0.3s ease-in-out;
            z-index: 20;
            pointer-events: none;
            display: flex;
            flex-wrap: wrap;
            align-items: center;
            justify-content: center;
            align-content: center;
            gap: 4px;
            padding: 6px;
            box-sizing: border-box;

            // 当只有一个按钮时（禁用状态），居中显示
            &:has(.status-icon-btn:only-child),
            &:has(.status-icon-btn:only-of-type) {
              .status-icon-btn {
                width: auto;
                min-width: auto;
                max-width: none;
                flex: 0 0 auto;
              }
            }

            /* 临时测试：如果悬停不工作，取消下面这行的注释来测试按钮是否可见 */
            /* opacity: 0.3; */

            .action-icon-btn {
              position: relative;
              width: calc(50% - 2px);
              height: 28px;
              min-width: calc(50% - 2px);
              max-width: calc(50% - 2px);
              min-height: 28px;
              background: rgba(0, 0, 0, 0.65);
              color: #fff !important;
              border-radius: 4px;
              display: flex !important;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              transition: all 0.3s;
              z-index: 25;
              pointer-events: auto;
              opacity: 1;
              visibility: visible;
              flex: 0 0 calc(50% - 2px);
              box-sizing: border-box;

              .anticon {
                font-size: 14px;
                color: #fff;
              }

              &:hover {
                background: rgba(0, 0, 0, 0.75);
                transform: scale(1.1);
              }

              &.preview-icon-btn {
                &:hover {
                  background: rgba(24, 144, 255, 0.75);
                }
              }

              &.delete-icon-btn {
                &:hover {
                  background: rgba(255, 77, 79, 0.75);
                }
              }

              &.set-primary-icon-btn {
                &:hover {
                  background: rgba(24, 144, 255, 0.75);
                }
              }

              &.status-icon-btn {
                width: calc(50% - 2px);
                min-width: calc(50% - 2px);
                max-width: calc(50% - 2px);
                padding: 0 2px;
                background: transparent;

                &:hover {
                  background: transparent;
                  transform: none;
                }

                :deep(.ant-switch) {
                  pointer-events: none;
                }
              }
            }
          }

          .primary-badge {
            position: absolute;
            top: 4px;
            left: 4px;
            background: #1890ff;
            color: #fff;
            font-size: 12px;
            padding: 2px 6px;
            border-radius: 2px;
            font-weight: 500;
            z-index: 1;
            pointer-events: none;
          }

          .disabled-badge {
            position: absolute;
            top: 4px;
            right: 4px;
            background: #ff4d4f;
            color: #fff;
            font-size: 12px;
            padding: 2px 6px;
            border-radius: 2px;
            font-weight: 500;
            z-index: 1;
            pointer-events: none;
          }
        }

        .image-name {
          padding: 4px 8px;
          font-size: 12px;
          color: #666;
          text-align: center;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          background: #fafafa;
        }
      }
    }

    .upload-item {
      width: 104px;
      display: inline-block;
      vertical-align: top;

      :deep(.ant-upload) {
        width: 104px;
        height: 104px;
        margin: 0;
        display: block;
      }

      :deep(.ant-upload-select) {
        width: 104px;
        height: 104px;
        border: 1px dashed #d9d9d9;
        border-radius: 4px;
        transition: all 0.3s;
        display: block;

        &:hover {
          border-color: #1890ff;
        }
      }
    }
  }
</style>

<style lang="less">
  .preview-modal {
    .ant-modal-body {
      padding: 0;
      position: relative;
      overflow: hidden;
    }

    .ant-modal-content {
      overflow: hidden;
    }

    .ant-modal-close {
      top: 0;
      right: 0;
      z-index: 1000;
      width: 40px;
      height: 40px;
      background: rgba(0, 0, 0, 0.6);
      border-radius: 0 8px 0 0;
    }

    .ant-modal-close-x {
      width: 40px;
      height: 40px;
      line-height: 40px;
      color: #fff;
      font-size: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s;

      &:hover {
        color: #fff;
        background: rgba(0, 0, 0, 0.8);
        transform: scale(1.1);
      }
    }

    .preview-container {
      position: relative;
      width: 100%;
      min-height: 400px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #000;
      outline: none;

      .preview-image-wrapper {
        position: relative;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;

        .preview-image {
          max-width: 100%;
          max-height: 70vh;
          object-fit: contain;
          display: block;
          width: auto;
          height: auto;
        }

        .preview-info {
          position: absolute;
          bottom: 20px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(0, 0, 0, 0.6);
          color: #fff;
          padding: 8px 16px;
          border-radius: 4px;
          font-size: 14px;
          z-index: 10;
        }
      }

      .preview-nav-btn {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        width: 48px;
        height: 48px;
        background: rgba(0, 0, 0, 0.6);
        border: none;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.3s;
        z-index: 10;
        color: #fff;
        font-size: 20px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

        &:hover:not(.disabled) {
          background: rgba(0, 0, 0, 0.8);
          transform: translateY(-50%) scale(1.1);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
        }

        &.disabled {
          opacity: 0.3;
          cursor: not-allowed;
        }

        &.prev-btn {
          left: 10px;
        }

        &.next-btn {
          right: 10px;
        }
      }
    }
  }
</style>
